import { BotIcon, PhoneForwardedIcon, PhoneIcon, UserPlusIcon, XCircleIcon } from 'lucide-react';

import { EscalationCallout } from '@/app/(app)/voice-agent/tab-routing';
import { ToggleButton } from '@/components/atoms/toggle-button';
import { FeatureFlagGuard } from '@/components/providers/feature-flag-provider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { useVoiceSettingsCtx } from './provider';

export function NewCustomerCreationSettings() {
  const {
    settings: { customer_creation },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();

  const onChange = (v: Partial<typeof customer_creation>) => {
    onSettingsChange({ customer_creation: { ...customer_creation, ...v } });
  };

  return (
    <div className='flex w-full flex-col gap-4'>
      <Card>
        <CardHeader>
          <CardTitle>New Customer Creation</CardTitle>
          <CardDescription>Define what happens if the caller is not an existing customer.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex flex-col gap-3'>
            <Label className='px-1'>How should new customers be handled?</Label>
            <div className='grid grid-cols-3 gap-2'>
              <FeatureFlagGuard feature='/voice-agent/settings/customer/create'>
                <ToggleButton
                  selected={customer_creation.action.handling === 'ENABLED'}
                  toggle={() => onChange({ action: { ...customer_creation.action, handling: 'ENABLED' } })}
                  layout='vertical'
                  className='py-4'
                >
                  <UserPlusIcon className='size-4' />
                  Create Account
                </ToggleButton>
              </FeatureFlagGuard>
              <ToggleButton
                selected={customer_creation.action.handling === 'NOTIFY_REPRESENTATIVE'}
                toggle={() => onChange({ action: { ...customer_creation.action, handling: 'NOTIFY_REPRESENTATIVE' } })}
                layout='vertical'
                className='py-4'
              >
                <PhoneForwardedIcon className='size-4' />
                Notify Representative
              </ToggleButton>
              <ToggleButton
                selected={customer_creation.action.handling === 'DISABLED'}
                toggle={() => onChange({ action: { ...customer_creation.action, handling: 'DISABLED' } })}
                layout='vertical'
                className='py-4'
              >
                <XCircleIcon className='size-4' />
                Disabled
              </ToggleButton>
            </div>
          </div>
        </CardContent>
      </Card>

      {customer_creation.action.handling === 'ENABLED' && (
        <>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between gap-2'>
              <CardTitle>Sell subscriptions before account creation</CardTitle>
              <Switch
                checked={customer_creation.sales.handling === 'ENABLED'}
                onCheckedChange={(checked) =>
                  onChange({ sales: { ...customer_creation.sales, handling: checked ? 'ENABLED' : 'DISABLED' } })
                }
              />
            </CardHeader>
            {customer_creation.sales.handling === 'ENABLED' && (
              <CardContent>
                <div className='space-y-2'>
                  <Label className='px-1'>Sales Script</Label>
                  <Textarea
                    placeholder='Enter the script the agent should use when selling subscriptions to new customers...'
                    className='min-h-[100px]'
                    value={customer_creation.sales.script_enabled}
                    onChange={(e) =>
                      onChange({ sales: { ...customer_creation.sales, script_enabled: e.target.value } })
                    }
                  />
                  <div className='text-sm text-muted-foreground'>
                    The script the Voice Agent will use when selling subscriptions before creating a new customer
                    account.
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Customer Information Collection</CardTitle>
              <CardDescription>Configure which information to collect from new customers.</CardDescription>
            </CardHeader>
            <CardContent className='flex flex-col gap-3'>
              <div className='flex w-full items-center'>
                <div className='space-y-1'>
                  <Label>Collect Full Name</Label>
                  <div className='text-sm text-muted-foreground'>Ask for the customer&apos;s first and last name.</div>
                </div>
                <Switch
                  className='ml-auto'
                  checked={customer_creation.collect_full_name}
                  onCheckedChange={(checked) => onChange({ collect_full_name: checked })}
                />
              </div>
              <Separator />
              <div className='flex w-full items-center'>
                <div className='space-y-1'>
                  <Label>Collect Phone Number</Label>
                  <div className='text-sm text-muted-foreground'>Request the customer&apos;s primary phone number.</div>
                </div>
                <Switch
                  className='ml-auto'
                  checked={customer_creation.collect_phone_number}
                  onCheckedChange={(checked) => onChange({ collect_phone_number: checked })}
                />
              </div>
              <Separator />
              <div className='flex w-full items-center'>
                <div className='space-y-1'>
                  <Label>Collect Email Address</Label>
                  <div className='text-sm text-muted-foreground'>Ask for the customer&apos;s email address.</div>
                </div>
                <Switch
                  className='ml-auto'
                  checked={customer_creation.collect_email_address}
                  onCheckedChange={(checked) => onChange({ collect_email_address: checked })}
                />
              </div>
              <Separator />
              <div className='flex w-full items-center'>
                <div className='space-y-1'>
                  <Label>Collect Service Address</Label>
                  <div className='text-sm text-muted-foreground'>
                    Collect the address where services will be provided.
                  </div>
                </div>
                <Switch
                  className='ml-auto'
                  checked={customer_creation.collect_service_address}
                  onCheckedChange={(checked) => onChange({ collect_service_address: checked })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>After Account Creation</CardTitle>
              <CardDescription>Define what should happen after a new customer account is created.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className='flex flex-col gap-3'>
                <Label className='px-1'>What should happen next?</Label>
                <div className='grid grid-cols-3 gap-2'>
                  <ToggleButton
                    selected={customer_creation.after_creation_action === 'CONTINUE_TO_BOOK_APPOINTMENT'}
                    toggle={() => onChange({ after_creation_action: 'CONTINUE_TO_BOOK_APPOINTMENT' })}
                    layout='vertical'
                    className='py-4'
                  >
                    <BotIcon className='size-4' />
                    Book Appointment
                  </ToggleButton>
                  <ToggleButton
                    selected={customer_creation.after_creation_action === 'NOTIFY_REPRESENTATIVE'}
                    toggle={() => onChange({ after_creation_action: 'NOTIFY_REPRESENTATIVE' })}
                    layout='vertical'
                    className='py-4'
                  >
                    <PhoneForwardedIcon className='size-4' />
                    Notify Representative
                  </ToggleButton>
                  <ToggleButton
                    selected={customer_creation.after_creation_action === 'END_CALL'}
                    toggle={() => onChange({ after_creation_action: 'END_CALL' })}
                    layout='vertical'
                    className='py-4'
                  >
                    <PhoneIcon className='size-4' />
                    End Call
                  </ToggleButton>
                </div>

                {customer_creation.after_creation_action === 'CONTINUE_TO_BOOK_APPOINTMENT' && (
                  <div className='mt-4 rounded-md border bg-muted/50 p-4'>
                    <div className='text-sm text-muted-foreground'>
                      Agent will now proceed with the standard appointment booking flow using the configurations defined
                      in the &quot;Actions &gt; Book Appointment&quot; section.
                    </div>
                  </div>
                )}

                {customer_creation.after_creation_action === 'NOTIFY_REPRESENTATIVE' && (
                  <div className='mt-4'>
                    <EscalationCallout />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {customer_creation.action.handling === 'NOTIFY_REPRESENTATIVE' && (
        <Card>
          <CardHeader>
            <CardTitle>Representative Notification</CardTitle>
            <CardDescription>Configure the notification when a new customer calls.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <EscalationCallout />
              <div className='space-y-2'>
                <Label className='px-1'>Script</Label>
                <Textarea
                  placeholder='Enter the script the agent should use when notifying a representative about a new customer...'
                  className='min-h-[100px]'
                  value={customer_creation.action.script_notify}
                  onChange={(e) => onChange({ action: { ...customer_creation.action, script_notify: e.target.value } })}
                />
                <div className='text-sm text-muted-foreground'>
                  The script the Voice Agent will use while notifying a representative.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {customer_creation.action.handling === 'DISABLED' && (
        <Card>
          <CardHeader>
            <CardTitle>New Customer Response</CardTitle>
            <CardDescription>Configure what the agent says when new customer creation is disabled.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-2'>
              <Label className='px-1'>Script</Label>
              <Textarea
                placeholder='Enter the script the agent should use when new customer creation is disabled...'
                value={customer_creation.action.script_disabled}
                onChange={(e) => onChange({ action: { ...customer_creation.action, script_disabled: e.target.value } })}
                className='min-h-[100px]'
              />
              <div className='text-sm text-muted-foreground'>
                The script the Voice Agent will use when declining to create a new customer account.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
